import _ from 'lodash'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material'
import Diversity3Icon from '@mui/icons-material/Diversity3'
import ExploreIcon from '@mui/icons-material/Explore'
import GroupAddIcon from '@mui/icons-material/GroupAdd'

import TextInput from '@/components/ui/inputs/TextInput'

import { useGetNetworksQuery } from '@/core/services/network'

import { MAIN_HEADER_HEIGHT } from '@/layouts/main'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useDebounceCallback } from 'usehooks-ts'
import { GetNetworksParams } from '@/core/api/network'

export default function NetworksNavDrawer() {
  const location = useLocation()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const [getBaseArgs, setGetBaseArgs] = useState<GetNetworksParams>({ createdBy: authUser?._id })

  const { data: managedNetworks } = useGetNetworksQuery(getBaseArgs, { skip: !authUser })

  // TODO : this only changes the joined network, how can we make it so that it influentates on the discover page network
  const searchNetwork = useDebounceCallback((value: string) => {
    setGetBaseArgs({ ...getBaseArgs, name: value })
  }, 300)

  const navItems = useMemo(
    () => [
      { id: 'discover', label: t('page.networks.navItem.discover'), icon: <ExploreIcon />, link: '/networks/discover' },
      { id: 'joins', label: t('page.networks.navItem.joins'), icon: <Diversity3Icon />, link: '/networks/joins' },
      { id: 'invitations', label: t('page.networks.navItem.invitations'), icon: <GroupAddIcon />, link: '/networks/invitations' },
    ],
    [t]
  )

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: 360,
        minHeight: 'inherit',
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: 360,
          boxSizing: 'border-box',
          position: 'absolute',
        },
      }}
    >
      <Toolbar sx={{ minHeight: 'unset !important', height: MAIN_HEADER_HEIGHT }} />
      <Box mt="20px" mx={2} mb="12px">
        <Stack>
          <Typography component="h1" sx={{ fontSize: 24, fontWeight: 700, lineHeight: '28px' }}>
            {t('page.networks.title')}
          </Typography>
        </Stack>
      </Box>
      <Box mt="4px" mb="12px">
        <Stack px={2}>
          <TextInput
            fullWidth
            name="search-networks"
            placeholder={t('page.networks.searchInput.placeholder')}
            onChange={(e) => {
              searchNetwork(e.target.value)
            }}
          />
        </Stack>
      </Box>
      <Stack
        flex={1}
        sx={{
          minHeight: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          overscrollBehaviorY: 'contain',
        }}
      >
        <Box mt={0.5} mb={1}>
          <List>
            {navItems.map(({ id, label, link, icon }) => (
              <ListItem key={id} disablePadding>
                <ListItemButton component={Link} selected={location.pathname === link} to={link}>
                  <ListItemIcon>{icon}</ListItemIcon>
                  <ListItemText primary={label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <Box mt={1} mx={2}>
            <Button component={Link} to="/networks/create" fullWidth variant="contained">
              {t('common:button.create.label')}
            </Button>
          </Box>
        </Box>
        <Divider sx={{ mx: 2, my: 1 }} />
        <Stack direction="row" my="-6px" py="12px" px={2}>
          <Typography component="span" sx={{ fontSize: 17, fontWeight: 600, lineHeight: '20px' }}>
            {t('page.networks.yourManagedNetworks')}
          </Typography>
        </Stack>
        <List dense sx={{ width: '100%', overflow: 'auto' }}>
          {_.map(managedNetworks, (managedNetwork) => {
            return (
              <ListItem key={managedNetwork._id} disablePadding>
                <ListItemButton component={Link} to={`/networks/${managedNetwork._id}`}>
                  <ListItemAvatar>
                    <Avatar
                      alt={`Avatar`}
                      // src={`/static/images/avatar/${value + 1}.jpg`}
                    />
                  </ListItemAvatar>
                  <ListItemText primary={managedNetwork.name} secondary={`Created at ${dayjs(managedNetwork.createdAt).format('DD/MM/YYYY')}`} />
                </ListItemButton>
              </ListItem>
            )
          })}
        </List>
      </Stack>
    </Drawer>
  )
}
